import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Save, AlertCircle, ToggleLeft, ToggleRight, RefreshCw, Copy, Check, QrCode, Download, Printer } from 'lucide-react';
import QRCode from 'qrcode';

interface Department {
  department_id: string;
  name: string;
  name_tamil?: string;
  registration_code?: string;
}

const RegistrationSettingsPage: React.FC = () => {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const [isModuleEnabled, setIsModuleEnabled] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [copiedDepartment, setCopiedDepartment] = useState<string | null>(null);
  const [showQRCode, setShowQRCode] = useState<string | null>(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const printFrameRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    checkAuthorization();
  }, []);

  const checkAuthorization = async () => {
    try {
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select(`
          roles!inner(
            role_name
          )
        `)
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

      const hasAccess = userRoles?.some(ur => 
        ur.roles.role_name === 'admin'
      ) ?? false;

      setIsAuthorized(hasAccess);
      if (!hasAccess) {
        navigate('/');
      } else {
        loadData();
      }
    } catch (error) {
      console.error('Error checking authorization:', error);
      setError('Failed to verify access permissions');
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Fetch module status
      const { data: moduleSettings, error: moduleError } = await supabase
        .from('system_settings')
        .select('setting_value')
        .eq('setting_name', 'public_registration_enabled')
        .maybeSingle();
      
      if (moduleError) {
        console.error('Error fetching module settings:', moduleError);
      }
      
      // If data exists and has enabled property set to true, module is enabled
      setIsModuleEnabled(moduleSettings?.setting_value?.enabled === true);
      
      // Fetch departments
      const { data: departmentsData, error: departmentsError } = await supabase
        .from('departments')
        .select('*')
        .order('name');
      
      if (departmentsError) throw departmentsError;
      
      setDepartments(departmentsData || []);
      setError(null);
    } catch (error: any) {
      console.error('Error loading data:', error);
      setError(error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleModuleStatus = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      // Check if setting exists
      const { data: existingSettings, error: checkError } = await supabase
        .from('system_settings')
        .select('setting_id')
        .eq('setting_name', 'public_registration_enabled')
        .maybeSingle();
      
      if (checkError) {
        console.error('Error checking settings:', checkError);
      }
      
      if (existingSettings) {
        // Update existing setting
        const { error: updateError } = await supabase
          .from('system_settings')
          .update({
            setting_value: { enabled: !isModuleEnabled },
            updated_at: new Date()
          })
          .eq('setting_name', 'public_registration_enabled');
        
        if (updateError) throw updateError;
      } else {
        // Create new setting
        const { error: insertError } = await supabase
          .from('system_settings')
          .insert([{
            setting_name: 'public_registration_enabled',
            setting_value: { enabled: !isModuleEnabled }
          }]);
        
        if (insertError) throw insertError;
      }
      
      setIsModuleEnabled(!isModuleEnabled);
      setSuccess(t('registrationSettings.moduleToggleSuccess'));
    } catch (error: any) {
      console.error('Error toggling module status:', error);
      setError(error.message || 'Failed to toggle module status');
    } finally {
      setSaving(false);
    }
  };

  const generateRegistrationCode = async (departmentId: string) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      // Generate a random 6-character alphanumeric code
      const code = Math.random().toString(36).substring(2, 8).toUpperCase();
      
      const { error } = await supabase
        .from('departments')
        .update({ registration_code: code })
        .eq('department_id', departmentId);
      
      if (error) throw error;
      
      // Refresh departments data
      const { data: departmentsData, error: departmentsError } = await supabase
        .from('departments')
        .select('*')
        .order('name');
      
      if (departmentsError) throw departmentsError;
      
      setDepartments(departmentsData || []);
      setSuccess(t('registrationSettings.codeGenerateSuccess'));
    } catch (error: any) {
      console.error('Error generating registration code:', error);
      setError(error.message || 'Failed to generate registration code');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (code: string, departmentId: string) => {
    navigator.clipboard.writeText(code)
      .then(() => {
        setCopiedDepartment(departmentId);
        setTimeout(() => setCopiedDepartment(null), 2000);
      })
      .catch(err => {
        console.error('Failed to copy code:', err);
      });
  };

  const generateQRCode = async (departmentId: string) => {
    try {
      const department = departments.find(d => d.department_id === departmentId);
      if (!department || !department.registration_code) {
        setError('Department has no registration code');
        return;
      }

      setSelectedDepartment(department);
      
      // Instead of encoding just the code, encode the full URL with the code parameter
      const registrationUrl = `${window.location.origin}/registrationform?code=${department.registration_code}`;
      
      const qrCode = await QRCode.toDataURL(registrationUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });
      
      setQrCodeDataUrl(qrCode);
      setShowQRCode(departmentId);
    } catch (error: any) {
      console.error('Error generating QR code:', error);
      setError(error.message || 'Failed to generate QR code');
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeDataUrl) return;
    
    const link = document.createElement('a');
    link.href = qrCodeDataUrl;
    link.download = `registration-qr-${selectedDepartment?.name || 'code'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const printQrCode = () => {
    if (!qrCodeDataUrl || !selectedDepartment) return;
    
    // Create HTML content for printing
    const printContent = generatePrintHTML();
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to print the QR code');
      return;
    }
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Auto print after a delay to ensure content is loaded
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // Generate the HTML content for printing
  const generatePrintHTML = () => {
    if (!selectedDepartment || !qrCodeDataUrl) return '';
    
    // Get translations based on current language
    const getTranslatedText = (en: string, nn: string, ta: string) => {
      if (i18n.language === 'ta') return ta;
      if (i18n.language === 'nn') return nn;
      return en;
    };
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Registration QR Code - ${selectedDepartment.name}</title>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Tamil:wght@400;700&family=Inter:wght@400;500;600;700&display=swap');
          
          :root {
            --primary-color: #2563eb;
            --primary-light: #dbeafe;
            --text-dark: #1f2937;
            --text-light: #6b7280;
            --bg-light: #f9fafb;
            --border-color: #e5e7eb;
          }
          
          * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
          }
          
          body {
            font-family: 'Inter', 'Noto Sans Tamil', sans-serif;
            line-height: 1.5;
            color: var(--text-dark);
            background-color: white;
            padding: 0;
            margin: 0;
          }
          
          .container {
            max-width: 210mm;
            margin: 0 auto;
            padding: 20px;
          }
          
          .card {
            border: 1px solid var(--border-color);
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 10px 15px rgba(0, 0, 0, 0.03);
            margin-bottom: 30px;
            background-color: white;
          }
          
          .card-header {
            background-color: var(--primary-color);
            color: white;
            padding: 16px 20px;
            font-size: 20px;
            font-weight: 600;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }
          
          .card-body {
            padding: 24px;
          }
          
          .department-name {
            font-size: 28px;
            font-weight: 700;
            margin-bottom: 8px;
            color: var(--text-dark);
          }
          
          .department-name-tamil {
            font-size: 24px;
            margin-bottom: 24px;
            color: var(--text-dark);
            font-family: 'Noto Sans Tamil', sans-serif;
          }
          
          .qr-container {
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 20px 0 30px;
          }
          
          .qr-code {
            width: 220px;
            height: 220px;
            padding: 10px;
            background-color: white;
            border: 1px solid var(--border-color);
            border-radius: 12px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          }
          
          .qr-code img {
            width: 100%;
            height: 100%;
          }
          
          .code-display {
            text-align: center;
            margin: 20px 0;
          }
          
          .code-label {
            font-size: 14px;
            color: var(--text-light);
            margin-bottom: 8px;
          }
          
          .code-value {
            font-family: monospace;
            font-size: 32px;
            font-weight: 700;
            letter-spacing: 2px;
            background-color: var(--primary-light);
            color: var(--primary-color);
            padding: 12px 24px;
            border-radius: 8px;
            display: inline-block;
          }
          
          .instructions {
            background-color: var(--bg-light);
            border-radius: 12px;
            padding: 24px;
            margin-top: 30px;
          }
          
          .instructions-title {
            font-size: 18px;
            font-weight: 600;
            margin-bottom: 16px;
            color: var(--text-dark);
            display: flex;
            align-items: center;
          }
          
          .instructions-title::before {
            content: '';
            display: inline-block;
            width: 8px;
            height: 20px;
            background-color: var(--primary-color);
            margin-right: 12px;
            border-radius: 4px;
          }
          
          .steps {
            list-style-type: none;
            counter-reset: steps;
            margin-left: 10px;
          }
          
          .steps li {
            position: relative;
            padding-left: 40px;
            margin-bottom: 16px;
            counter-increment: steps;
          }
          
          .steps li::before {
            content: counter(steps);
            position: absolute;
            left: 0;
            top: 0;
            width: 28px;
            height: 28px;
            background-color: var(--primary-color);
            color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;
            font-size: 14px;
          }
          
          .url-note {
            margin-top: 20px;
            padding: 16px;
            background-color: white;
            border: 1px solid var(--border-color);
            border-radius: 8px;
            font-size: 14px;
          }
          
          .url-value {
            font-family: monospace;
            word-break: break-all;
            background-color: var(--bg-light);
            padding: 8px;
            border-radius: 4px;
            margin-top: 8px;
            font-size: 13px;
          }
          
          .footer {
            margin-top: 40px;
            text-align: center;
            color: var(--text-light);
            font-size: 12px;
            border-top: 1px solid var(--border-color);
            padding-top: 20px;
          }
          
          @media print {
            .no-print {
              display: none;
            }
            
            body {
              background-color: white;
            }
            
            .container {
              padding: 0;
              width: 100%;
            }
            
            .card {
              box-shadow: none;
              border: none;
            }
            
            @page {
              size: A4;
              margin: 1cm;
            }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="card">
            <div class="card-header">
              ${getTranslatedText(
                'Registration QR Code',
                'Registrerings-QR-kode',
                'பதிவு QR குறியீடு'
              )}
            </div>
            <div class="card-body">
              <div class="department-name">${selectedDepartment.name}</div>
              ${selectedDepartment.name_tamil ? 
                `<div class="department-name-tamil">${selectedDepartment.name_tamil}</div>` : 
                ''}
              
              <div class="qr-container">
                <div class="qr-code">
                  <img src="${qrCodeDataUrl}" alt="Registration QR Code">
                </div>
              </div>
              
              <div class="code-display">
                <div class="code-label">
                  ${getTranslatedText(
                    'Department Registration Code',
                    'Avdelingsregistreringskode',
                    'வளாக பதிவு குறியீடு'
                  )}
                </div>
                <div class="code-value">
                  ${selectedDepartment.registration_code}
                </div>
              </div>
              
              <div class="instructions">
                <div class="instructions-title">
                  ${getTranslatedText(
                    'How to Register',
                    'Korleis registrere',
                    'பதிவு செய்வது எப்படி'
                  )}
                </div>
                <ol class="steps">
                  <li>
                    ${getTranslatedText(
                      'Scan the QR code with your smartphone camera',
                      'Skann QR-koden med smarttelefonen ditt kamera',
                      'உங்கள் ஸ்மார்ட்போன் கேமராவுடன் QR குறியீட்டை ஸ்கேன் செய்யவும்'
                    )}
                  </li>
                  <li>
                    ${getTranslatedText(
                      'You will be directed to the registration form',
                      'Du vil bli dirigert til registreringsskjemaet',
                      'நீங்கள் பதிவுப் படிவத்திற்கு வழிநடத்தப்படுவீர்கள்'
                    )}
                  </li>
                  <li>
                    ${getTranslatedText(
                      'Fill in your details and select competitions',
                      'Fyll inn detaljane dine og vel konkurransar',
                      'உங்கள் விவரங்களை நிரப்பி போட்டிகளைத் தேர்ந்தெடுக்கவும்'
                    )}
                  </li>
                  <li>
                    ${getTranslatedText(
                      'Submit your registration',
                      'Send inn registreringa di',
                      'உங்கள் பதிவை சமர்ப்பிக்கவும்'
                    )}
                  </li>
                </ol>
                
                <div class="url-note">
                  ${getTranslatedText(
                    'Alternatively, you can visit the following URL and enter the code shown above:',
                    'Alternativt kan du besøke følgjande URL og skrive inn koden vist ovanfor:',
                    'அல்லது, பின்வரும் URL ஐப் பார்வையிட்டு மேலே காட்டப்பட்டுள்ள குறியீட்டை உள்ளிடலாம்:'
                  )}
                  <div class="url-value">${window.location.origin}/registrationform</div>
                </div>
              </div>
              
              <div class="footer">
                ${getTranslatedText(
                  'Generated on',
                  'Generert den',
                  'உருவாக்கப்பட்ட தேதி'
                )} ${new Date().toLocaleDateString()} | Annai Sports Management System
              </div>
            </div>
          </div>
        </div>
        
        <div class="no-print" style="text-align: center; margin: 20px 0;">
          <button onclick="window.print()" style="padding: 10px 20px; background-color: #2563eb; color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 500;">
            ${getTranslatedText('Print', 'Skriv ut', 'அச்சிடு')}
          </button>
          <button onclick="window.close()" style="padding: 10px 20px; background-color: #f3f4f6; color: #1f2937; border: 1px solid #e5e7eb; border-radius: 6px; margin-left: 10px; cursor: pointer; font-weight: 500;">
            ${getTranslatedText('Close', 'Lukk', 'மூடு')}
          </button>
        </div>
        
        <script>
          // Auto print
          setTimeout(() => window.print(), 500);
        </script>
      </body>
      </html>
    `;
  };

  if (!isAuthorized) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('registrationSettings.title')}</h1>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4">
          <div className="flex">
            <Check className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-700">{success}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {t('registrationSettings.moduleSettings')}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            {t('registrationSettings.moduleDescription')}
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-base font-medium text-gray-900">
                {t('registrationSettings.publicRegistration')}
              </h4>
              <p className="text-sm text-gray-500">
                {isModuleEnabled 
                  ? t('registrationSettings.moduleEnabled') 
                  : t('registrationSettings.moduleDisabled')}
              </p>
            </div>
            <button
              onClick={toggleModuleStatus}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              style={{ backgroundColor: 'transparent' }}
            >
              {isModuleEnabled ? (
                <ToggleRight className="h-10 w-10 text-green-600" />
              ) : (
                <ToggleLeft className="h-10 w-10 text-gray-400" />
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {t('registrationSettings.departmentCodes')}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            {t('registrationSettings.departmentCodesDescription')}
          </p>
        </div>
        <div className="border-t border-gray-200">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('registrationSettings.table.department')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('registrationSettings.table.tamilName')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('registrationSettings.table.registrationCode')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('registrationSettings.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {departments.map((department) => (
                  <tr key={department.department_id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {department.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {department.name_tamil || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                        {department.registration_code || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => generateRegistrationCode(department.department_id)}
                          disabled={saving}
                          className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
                        >
                          <RefreshCw className="h-4 w-4 mr-1" />
                          {t('registrationSettings.generateCode')}
                        </button>
                        {department.registration_code && (
                          <>
                            <button
                              onClick={() => copyToClipboard(department.registration_code!, department.department_id)}
                              className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              {copiedDepartment === department.department_id ? (
                                <>
                                  <Check className="h-4 w-4 mr-1 text-green-500" />
                                  {t('registrationSettings.copied')}
                                </>
                              ) : (
                                <>
                                  <Copy className="h-4 w-4 mr-1" />
                                  {t('registrationSettings.copy')}
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => generateQRCode(department.department_id)}
                              className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              <QrCode className="h-4 w-4 mr-1" />
                              QR Code
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {t('registrationSettings.publicRegistrationLink')}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            {t('registrationSettings.publicRegistrationLinkDescription')}
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          <div className="flex items-center">
            <input
              type="text"
              readOnly
              value={`${window.location.origin}/registrationform`}
              className="flex-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 text-gray-500 sm:text-sm"
            />
            <button
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/registrationform`);
                setSuccess(t('registrationSettings.linkCopied'));
                setTimeout(() => setSuccess(null), 2000);
              }}
              className="ml-3 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Copy className="h-4 w-4 mr-2" />
              {t('registrationSettings.copyLink')}
            </button>
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRCode && qrCodeDataUrl && selectedDepartment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                Registration QR Code - {selectedDepartment.name}
              </h2>
              <button
                onClick={() => setShowQRCode(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="flex flex-col items-center">
              <img 
                src={qrCodeDataUrl} 
                alt="Registration QR Code" 
                className="w-64 h-64 object-contain border border-gray-200 rounded-lg"
              />
              
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600 mb-2">
                  Department Code: <span className="font-mono font-bold">{selectedDepartment.registration_code}</span>
                </p>
                <p className="text-sm text-gray-600">
                  Scan this QR code to go directly to the registration form with the department code pre-filled.
                </p>
              </div>
              
              <div className="mt-6 flex flex-wrap gap-3 justify-center">
                <button
                  onClick={downloadQRCode}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download QR Code
                </button>
                <button
                  onClick={printQrCode}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print with Instructions
                </button>
                <button
                  onClick={() => setShowQRCode(null)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Hidden iframe for printing */}
      <iframe 
        ref={printFrameRef}
        style={{ display: 'none' }}
        title="Print Frame"
      />
    </div>
  );
};

export default RegistrationSettingsPage;